import OfflineCache from 'utils/OfflineCache';

// Patch Backbone.ajax to persist GET responses to IndexedDB and serve stale
// data on network failure.  Mirrors the monorepo HttpClient Axios interceptor
// pattern: cache on success, recover from cache on ERR_NETWORK (status 0).
export default function patchBackboneAjax() {
  const _originalBackboneAjax = Backbone.ajax.bind(Backbone);

  Backbone.ajax = function(options) {
    const method = (options.type || options.method || 'GET').toUpperCase();

    if (method === 'GET') {
      // Build canonical cache key from URL + query params.
      // Guard: options.data may be a string, Backbone model, or array on some
      // call sites — only call $.param on plain objects to avoid touching React
      // internals when jQuery iterates function-valued properties.
      const rawData = options.data;
      const data = rawData && typeof rawData === 'object' && !Array.isArray(rawData)
        ? rawData
        : {};
      // Normalize ISO datetimes (e.g. "2026-06-20T14:23:45-07:00") to YYYY-MM-DD
      // so cache keys are stable across page loads.  CalendarView passes full
      // timestamps via moment().format() that change every second.
      const keyData = {};
      Object.keys(data).forEach(k => {
        const v = data[k];
        // Skip non-primitive values (e.g. React component instances passed as
        // a routing "target" by DepartmentActions/LocationActions) — they must
        // not be serialised into a URL query string. Plain nested objects are
        // real query params ($.param brackets them: page[number]=2) and stay.
        if (v !== null && typeof v === 'object' && Object.getPrototypeOf(v) !== Object.prototype) return;
        if (typeof v === 'function') return;
        keyData[k] = (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) ? v.slice(0, 10) : v;
      });
      const queryString = Object.keys(keyData).length ? `?${$.param(keyData)}` : '';
      const url = options.url + queryString;
      const originalSuccess = options.success;
      const originalError = options.error;

      // Cache every successful GET response.  The cache-window check lives
      // inside OfflineCache.set() so it is applied consistently.
      options.success = function(responseData, status, xhr) {
        OfflineCache.set(url, responseData);
        if (originalSuccess) originalSuccess(responseData, status, xhr);
      };

      // On network failure (status 0 = ERR_NETWORK / ERR_INTERNET_DISCONNECTED),
      // serve from cache.  Mirrors the monorepo HttpClient error interceptor that
      // catches ERR_NETWORK and returns a synthetic 200 response from IndexedDB.
      options.error = function(jqXHR, textStatus, errorThrown) {
        if (jqXHR.status === 0) {
          OfflineCache.get(url).then(cached => {
            if (cached) {
              if (originalSuccess) originalSuccess(cached, 'success', {});
            } else if (originalError) {
              originalError(jqXHR, textStatus, errorThrown);
            }
          });
          return;
        }
        if (originalError) originalError(jqXHR, textStatus, errorThrown);
      };
    }

    return _originalBackboneAjax(options);
  };
}
